/**
 * First-login onboarding modal — language preferences.
 *
 * Shown when the authenticated user's `onboarded` flag is false. The user picks
 * their spoken languages (multi-select) and primary language (radio); on save
 * we POST `/api/auth/onboarding/complete` which flips `onboarded=true` server-
 * side so the modal never re-fires.
 *
 * Design choices (per user, 2026-04-27):
 *   1a — fires once via `onboarded` flag on user doc
 *   2c — "Skip" allowed; backend still flips onboarded=true so we don't ask again
 *   3a — pre-tick the browser language (e.g. `navigator.language="es-AR"` → `es`)
 *   4a — centered modal with backdrop blur
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Check, ArrowRight } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = "/api";

// Detect browser language and return a 2-letter code if it's supported.
const detectBrowserLang = (supported) => {
  const raw = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  const code = raw.split("-")[0];
  return supported.find(l => l.code === code)?.code || "en";
};

const OnboardingDialog = ({ user, onComplete }) => {
  const { t, i18n } = useTranslation();
  const [supported, setSupported] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [spoken, setSpoken] = useState([]);
  const [primary, setPrimary] = useState("");

  const open = !!user && user.onboarded === false;

  // Fetch the supported language list once when the modal becomes relevant.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/i18n/languages`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        const langs = data.languages || [];
        if (cancelled) return;
        setSupported(langs);
        const browserLang = detectBrowserLang(langs);
        setSpoken([browserLang]);
        setPrimary(browserLang);
      } catch {
        // Network failure: fall back to English defaults so the modal isn't useless.
        setSupported([{ code: "en", native: "English", flag: "🇺🇸" }]);
        setSpoken(["en"]);
        setPrimary("en");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggleSpoken = (code) => {
    setSpoken(prev => {
      if (prev.includes(code)) {
        // Don't let the user un-pick the primary — keeps state consistent.
        if (code === primary) return prev;
        return prev.filter(c => c !== code);
      }
      return [...prev, code];
    });
  };

  const submit = async (skipped = false) => {
    setSubmitting(true);
    try {
      const body = skipped
        ? { spoken_languages: [], primary_language: "" }
        : { spoken_languages: spoken, primary_language: primary };
      const res = await fetch(`${API}/auth/onboarding/complete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to save");
      // Snap the UI to the chosen language immediately.
      if (!skipped && primary) {
        try { i18n.changeLanguage(primary); } catch { /* non-fatal */ }
        try { localStorage.setItem("evoluras_lang", primary); } catch { /* non-fatal */ }
      }
      onComplete?.({
        spoken_languages: skipped ? ["en"] : spoken,
        primary_language: skipped ? "en" : primary,
      });
    } catch (e) {
      toast.error("Couldn't save your preferences. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSave = useMemo(
    () => spoken.length > 0 && !!primary && spoken.includes(primary),
    [spoken, primary],
  );

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* intentionally non-dismissable via overlay click */ }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="onboarding-language-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "rgba(167,139,250,0.18)" }}>
            <Sparkles className="w-4 h-4" style={{ color: "var(--color-brand-primary)" }} />
          </div>
          <div>
            <h2 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {t("onboarding.title")}
            </h2>
            <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
              {t("onboarding.subtitle")}
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-xs py-6 text-center" style={{ color: "var(--color-text-placeholder)" }}>
            {t("onboarding.loading")}
          </p>
        ) : (
          <div className="space-y-5">
            {/* Spoken languages — chips */}
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium mb-2"
                style={{ color: "var(--color-text-secondary)" }}>
                {t("onboarding.spokenLabel")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {supported.map(lang => {
                  const active = spoken.includes(lang.code);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => toggleSpoken(lang.code)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                      style={{
                        background: active ? "var(--color-brand-primary)" : "rgba(255,255,255,0.04)",
                        color: active ? "var(--color-text-on-brand)" : "var(--color-text-primary)",
                        border: `1px solid ${active ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                      }}
                      data-testid={`onb-spoken-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      {lang.native}
                      {active && <Check className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Primary language — segmented within the chosen spoken set */}
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium mb-2"
                style={{ color: "var(--color-text-secondary)" }}>
                {t("onboarding.primaryLabel")}
              </p>
              <p className="text-[11px] mb-2" style={{ color: "var(--color-text-placeholder)" }}>
                {t("onboarding.primaryHint")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {supported
                  .filter(lang => spoken.includes(lang.code))
                  .map(lang => {
                    const active = primary === lang.code;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setPrimary(lang.code)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                        style={{
                          background: active ? "rgba(167,139,250,0.20)" : "transparent",
                          color: "var(--color-text-primary)",
                          border: `1px solid ${active ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                          boxShadow: active ? "0 0 0 3px rgba(167,139,250,0.15)" : "none",
                        }}
                        data-testid={`onb-primary-${lang.code}`}
                      >
                        <span>{lang.flag}</span>
                        {lang.native}
                      </button>
                    );
                  })}
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={submitting}
                className="text-xs font-medium hover:underline transition-colors"
                style={{ color: "var(--color-text-secondary)" }}
                data-testid="onb-skip"
              >
                {t("onboarding.skip")}
              </button>
              <Button
                onClick={() => submit(false)}
                disabled={submitting || !canSave}
                className="rounded-full"
                style={{
                  background: "var(--color-brand-primary)",
                  color: "var(--color-text-on-brand)",
                }}
                data-testid="onb-save"
              >
                {submitting ? t("onboarding.saving") : t("onboarding.save")}
                {!submitting && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
              </Button>
            </div>

            {/* Privacy footnote */}
            <p className="text-[10px] text-center pt-1" style={{ color: "var(--color-text-placeholder)" }}>
              {t("onboarding.privacyFootnote")}{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: "var(--color-text-interactive)" }}
                data-testid="onb-privacy-link"
              >
                {t("common.privacyPolicy")}
              </a>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingDialog;
